import classNames from 'classnames'
import { useEffect, useState } from 'react'
import LazyLoad from 'react-lazyload'

import bannedImg from '@/assets/banned.png'
import unofficialImg from '@/assets/unofficial.png'
import Icon from '@/components/Icon/Icon'
import InteractiveTooltip from '@/components/InteractiveTooltip/InteractiveTooltip'
import OutboundLink from '@/components/OutboundLink/OutboundLink'
import toastService from '@/services/toast.service'
import { useAppSelector } from '@/store/hooks'
import { CardLabelVariantType, GenericCardType } from '@/types/card'
import { CardType, PageCardType } from '@/types/card'
import { bracketsArticleUrl } from '@/utils/constants'
import { useCard } from '@/utils/fetcher'
import { formatUrl } from '@/utils/helpers'
import usePatreonTier from '@/utils/usePatreonTier'

import styles from './Card.module.scss'
import CardButtons from './CardButtons/CardButtons'
import CardImage from './CardImage'
import CardLabel from './CardLabel/CardLabel'
import CardLoading from './CardLoading/CardLoading'
import CardPrices from './CardPrices/CardPrices'
import CardSalt from './CardSalt/CardSalt'

type Props = {
  background?: boolean
  card?: GenericCardType
  className?: string
  fullCard?: CardType
  isPreview?: boolean
  label?: React.ReactNode
  labelVariant?: CardLabelVariantType
  name?: string
  new?: boolean
  onlyBack?: boolean
  onlyFront?: boolean
  pageCommander?: PageCardType
  reverse?: boolean
  scalable?: boolean
  sideBySide?: boolean
  target?: string
}

const CardLazy = (props: Props) => {
  const { card, fullCard, isPreview, name, onlyBack, onlyFront, pageCommander, reverse, scalable, sideBySide, target } =
    props
  const alternateArt = useAppSelector((state) => state.user.alternateArt)
  const patreonTier = usePatreonTier()
  const [imageIsLoaded, setImageIsLoaded] = useState(false)
  const [isRotated, setIsRotated] = useState(false)
  const [isContentWarningHidden, setIsContentWarningHidden] = useState(false)

  useEffect(() => {
    setIsRotated(false)
    setIsContentWarningHidden(false)
  }, [card])

  // Fetch data
  const { data: fetchedData, error } = useCard({ card: card as GenericCardType, name })
  const data =
    fetchedData ||
    (fullCard ? { ...fullCard, url: fullCard.sanitized || formatUrl(fullCard.name || name || '') } : undefined)

  if (error) return isPreview ? <>Failed to load {<span className={styles.error}>{name}</span>}</> : null

  const { banned, content_warning: hasContentWarning, prices, unofficial } = data || {}
  const isGameChanger = data?.game_changer
  const isHot = (card?.trend_zscore ?? 0) >= 2
  const isNew = data?.new || props.new

  // image_uris
  let image_uris: string[] | undefined = undefined
  if (patreonTier === 'rare' && alternateArt && !name?.includes('|')) {
    if (card && card.cards && card.cards.length > 1 && card.is_partner) {
      image_uris = [0, 1].map((i) =>
        card.cards
          ? alternateArt[card.cards[i]?.url || '']
            ? alternateArt[card.cards[i]?.url || ''][0]
            : data
              ? data.image_uris[i]
              : ''
          : '',
      )
    } else if (data) {
      image_uris = alternateArt[data.url || data.sanitized || ''] || data.image_uris
    }
  } else if (data) {
    image_uris = data.image_uris
  }

  // If no url given, build a url from the name
  // If given url is null, pass in undefined (no link, Card is unclickable)
  // Otherwise, use the given url
  const url =
    card?.url !== undefined
      ? card.url === null
        ? undefined
        : card.url
      : `/${data?.legal_commander ? 'commanders' : 'cards'}/${formatUrl((name || data?.name)?.split('|')[0] || '')}`

  return (
    <>
      {banned ? (
        <div className={styles.banned}>
          <img alt='Banned' src={bannedImg.src} />
        </div>
      ) : unofficial ? (
        <div className={styles.banned}>
          <img alt='Unofficial' src={unofficialImg.src} />
        </div>
      ) : null}
      {data && (
        <>
          {hasContentWarning && !isContentWarningHidden && (
            <div className={styles.hasWarning}>
              <p className={styles.warningIcon}>
                <Icon icon='circleExclamation' />
              </p>
              <p className={styles.warningNote}>
                <strong>Please note:</strong> This card is not in compliance with Magic’s community standards as a
                result of racist artwork or text, the use of demographic stereotypes, and/or parallels to sensitive
                historical events.
              </p>
              <p>
                <button className='btn btn-outline-light btn-sm' onClick={() => setIsContentWarningHidden(true)}>
                  Show Card Image
                </button>
              </p>
              <p>
                <OutboundLink
                  className='btn btn-outline-light btn-sm'
                  url='https://magic.wizards.com/en/news/announcements/depictions-racism-magic-2020-06-10'
                >
                  More Info
                </OutboundLink>
              </p>
            </div>
          )}
          {!hasContentWarning && (
            <CardButtons
              card={data}
              isRotated={isRotated}
              pageCommander={pageCommander}
              scalable={scalable}
              setIsRotated={setIsRotated}
            />
          )}
          {image_uris && (
            <CardImage
              image_uris={image_uris}
              isRotated={isRotated}
              name={data.name}
              names={data.names}
              onLoad={() => setImageIsLoaded(true)}
              onlyBack={onlyBack}
              onlyFront={onlyFront}
              reverse={reverse}
              scalable={scalable}
              sideBySide={sideBySide}
              target={target}
              url={url}
            >
              {isGameChanger ? (
                <OutboundLink url={bracketsArticleUrl}>
                  <div className={styles.gameChanger}>
                    <span>GC</span>
                  </div>
                </OutboundLink>
              ) : isHot ? (
                <InteractiveTooltip
                  tooltipChildren={
                    <>
                      This card is{' '}
                      <OutboundLink url='https://edhrec.com/articles/introducing-the-math-behind-edhrecs-fire-and-ice-series'>
                        trending
                      </OutboundLink>
                      !<div className='text-white'></div>
                      <div>
                        {/* Show 1-4 fire emojis based on the trend_zscore */}
                        {Array.from(
                          { length: Math.min(4, Math.max(0, Math.floor((card!.trend_zscore ?? 0) - 1))) },
                          (_, i) => (
                            <span aria-label='fire' key={i} role='img'>
                              🔥
                            </span>
                          ),
                        )}
                      </div>
                      <div>Fire Score: {card!.trend_zscore!.toFixed(2)}</div>
                    </>
                  }
                >
                  <div className={styles.hot}>
                    <span>HOT</span>
                  </div>
                </InteractiveTooltip>
              ) : isNew ? (
                <div className={styles.new}>
                  <span>NEW</span>
                </div>
              ) : undefined}
              <CardSalt salt={data.salt} />
            </CardImage>
          )}
        </>
      )}
      {!imageIsLoaded && <CardLoading />}
      {prices && !hasContentWarning && <CardPrices card={data as CardType} scalable={scalable} />}
    </>
  )
}

const Card = (props: Props) => {
  const namesUnderCards = useAppSelector((state) => state.user.namesUnderCards)

  const name = props.card?.name || props.name || props.fullCard?.name || ''

  return (
    <div
      className={classNames(styles.container, props.className, {
        [styles.sideBySide]: props.sideBySide,
        [styles.scalable]: props.scalable,
      })}
    >
      {!namesUnderCards && (
        <div
          className={classNames(styles.nameWrapper, {
            [styles.scalableNameWrapper]: props.scalable,
          })}
          onClick={() => {
            toastService.createToast(`Copied ${name}`)
            navigator.clipboard.writeText(name)
          }}
        >
          <span className={styles.name}>{name}</span>
        </div>
      )}
      <LazyLoad className={classNames({ [styles.sideBySide]: props.sideBySide })} height={359.19} once>
        <CardLazy {...props} pageCommander={props.pageCommander} />
      </LazyLoad>
      {namesUnderCards && <div className={styles.nameUnderCard}>{name}</div>}
      {props.label || (
        <CardLabel card={props.card} labelVariant={props.labelVariant} pageCommander={props.pageCommander} />
      )}
    </div>
  )
}

export default Card
